export type AssetCategory = "checklist" | "review-tool" | "decision-matrix" | "worked-example";

export const ASSET_CATEGORY_ORDER: AssetCategory[] = ["checklist", "review-tool", "decision-matrix", "worked-example"];

export const ASSET_CATEGORIES: Record<AssetCategory, { label: string; blurb: string }> = {
  checklist: {
    label: "Checklists",
    blurb: "Point-by-point checks to run against a RAMS draft before it goes to a client or principal contractor.",
  },
  "review-tool": {
    label: "Review tools",
    blurb: "Reviewer-side tools for judging whether a RAMS is specific, coordinated and ready to accept.",
  },
  "decision-matrix": {
    label: "Decision matrices",
    blurb: "Quick matrices for deciding when a permit or extra control should be in place before work starts.",
  },
  "worked-example": {
    label: "Worked examples",
    blurb: "Before/after examples showing how generic paperwork becomes a site-specific, reviewable document.",
  },
};

export type PracticalAsset = {
  slug: string;
  title: string;
  description: string;
  category: AssetCategory;
  downloadHref: string;
  format: string;
  relatedGuideSlugs: string[];
  sourceLinks: { label: string; url: string; publisher: string }[];
  sections: { heading: string; bullets: string[] }[];
};

const commonSourceLinks = [
  {
    label: "Managing risks and risk assessment at work",
    url: "https://www.hse.gov.uk/simple-health-safety/risk/index.htm",
    publisher: "HSE",
  },
  {
    label: "Construction (Design and Management) Regulations 2015",
    url: "https://www.legislation.gov.uk/uksi/2015/51/contents/made",
    publisher: "legislation.gov.uk",
  },
];

export const PRACTICAL_ASSETS: PracticalAsset[] = [
  {
    slug: "rams-pre-submission-checklist",
    title: "RAMS Pre-Submission Checklist",
    description: "A 32-point final check before a contractor submits RAMS to a client or principal contractor.",
    category: "checklist",
    downloadHref: "/downloads/rams-pre-submission-checklist.md",
    format: "Markdown checklist",
    relatedGuideSlugs: [
      "what-to-include-in-a-rams-template",
      "how-to-make-rams-site-specific",
      "why-rams-get-rejected-by-principal-contractors",
      "what-is-a-rams-document",
    ],
    sourceLinks: commonSourceLinks,
    sections: [
      {
        heading: "Use this before submission",
        bullets: [
          "Confirm the task, site, dates, workers, supervisors and emergency contacts are specific.",
          "Check every high-risk activity has a matching method step and residual risk score.",
          "Confirm worker briefing, competent-person review and document version control are complete.",
        ],
      },
      {
        heading: "Best fit",
        bullets: [
          "Subcontractor pre-start packs.",
          "Principal-contractor document portals.",
          "Internal document review before RAMS issue.",
        ],
      },
    ],
  },
  {
    slug: "principal-contractor-rams-review-checklist",
    title: "Principal Contractor RAMS Review Checklist",
    description: "A PC-facing review tool for checking whether a subcontractor RAMS is coordinated, specific and ready to accept.",
    category: "review-tool",
    downloadHref: "/downloads/principal-contractor-rams-review-checklist.md",
    format: "Markdown checklist",
    relatedGuideSlugs: [
      "what-does-a-principal-contractor-check-in-a-rams",
      "who-writes-a-rams-and-who-approves-it",
      "competent-person-sign-off-for-rams",
      "construction-phase-plan-vs-rams",
    ],
    sourceLinks: [
      ...commonSourceLinks,
      {
        label: "Principal contractors: roles and responsibilities",
        url: "https://www.hse.gov.uk/construction/cdm/2015/principal-contractors.htm",
        publisher: "HSE",
      },
    ],
    sections: [
      {
        heading: "Review gates",
        bullets: [
          "Check the RAMS aligns with the construction phase plan, site rules and emergency arrangements.",
          "Confirm interfaces with other contractors, public areas, plant routes and permits are controlled.",
          "Record accepted, accepted with comments, revise and resubmit, or rejected.",
        ],
      },
      {
        heading: "Best fit",
        bullets: [
          "PC document control teams.",
          "Site managers reviewing subcontractor packs.",
          "Contractor self-checking before upload.",
        ],
      },
    ],
  },
  {
    slug: "permit-trigger-matrix",
    title: "Permit Trigger Matrix",
    description: "A quick matrix for deciding when permit-to-dig, hot work, isolation, confined space and lifting permits should be considered.",
    category: "decision-matrix",
    downloadHref: "/downloads/permit-trigger-matrix.md",
    format: "Markdown matrix",
    relatedGuideSlugs: [
      "what-is-a-permit-to-dig",
      "when-do-you-need-a-hot-work-permit",
      "construction-phase-plan-vs-rams",
    ],
    sourceLinks: [
      ...commonSourceLinks,
      {
        label: "Avoiding danger from underground services, HSG47",
        url: "https://www.hse.gov.uk/pubns/books/hsg47.htm",
        publisher: "HSE",
      },
      {
        label: "Construction fire safety",
        url: "https://www.hse.gov.uk/construction/faq-fire.htm",
        publisher: "HSE",
      },
    ],
    sections: [
      {
        heading: "Use this during planning",
        bullets: [
          "Identify permit triggers before the method statement is written.",
          "Record who issues, accepts and closes each permit.",
          "Link permit controls back into RAMS sequence steps.",
        ],
      },
    ],
  },
  {
    slug: "hot-work-permit-trigger-matrix",
    title: "Hot Work Permit Trigger Matrix",
    description: "A focused hot-work decision tool for welding, cutting, grinding, torch-on works and temporary heating.",
    category: "decision-matrix",
    downloadHref: "/downloads/hot-work-permit-trigger-matrix.md",
    format: "Markdown matrix",
    relatedGuideSlugs: [
      "when-do-you-need-a-hot-work-permit",
      "why-rams-get-rejected-by-principal-contractors",
      "what-to-include-in-a-rams-template",
    ],
    sourceLinks: [
      {
        label: "Construction fire safety",
        url: "https://www.hse.gov.uk/construction/faq-fire.htm",
        publisher: "HSE",
      },
      {
        label: "Fire safety in construction, HSG168",
        url: "https://www.hse.gov.uk/pubns/books/hsg168.htm",
        publisher: "HSE",
      },
      {
        label: "Regulatory Reform (Fire Safety) Order 2005",
        url: "https://www.legislation.gov.uk/uksi/2005/1541/contents/made",
        publisher: "legislation.gov.uk",
      },
    ],
    sections: [
      {
        heading: "Use this before hot works",
        bullets: [
          "Decide if a hot-work permit is required by the site, task, material, location or client rules.",
          "Record pre-work checks, fire watch duration, extinguishers, isolation and post-work monitoring.",
          "Feed the permit controls into the RAMS sequence rather than leaving them as a separate form.",
        ],
      },
    ],
  },
  {
    slug: "site-specific-rams-before-after-example",
    title: "Site-Specific RAMS Before/After Example",
    description: "A worked example showing how a generic RAMS paragraph becomes a site-specific, PC-reviewable control.",
    category: "worked-example",
    downloadHref: "/downloads/site-specific-rams-before-after-example.md",
    format: "Markdown example",
    relatedGuideSlugs: [
      "how-to-make-rams-site-specific",
      "why-rams-get-rejected-by-principal-contractors",
      "risk-assessment-vs-method-statement",
      "dynamic-risk-assessment-vs-rams",
    ],
    sourceLinks: commonSourceLinks,
    sections: [
      {
        heading: "Use this as a drafting pattern",
        bullets: [
          "Convert generic controls into named locations, named equipment and named responsibilities.",
          "Show how the control appears in both the risk assessment and method statement.",
          "Use the same before/after structure to improve rejected RAMS.",
        ],
      },
    ],
  },
];

export function getAsset(slug: string): PracticalAsset | undefined {
  return PRACTICAL_ASSETS.find((asset) => asset.slug === slug);
}
